import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { findDashViolations } from "./rules";

/**
 * Latin runs inside Arabic copy always render smaller.
 *
 * The size is in `em`, not a fixed step, so ONE rule holds at every type scale:
 * a term inside a 5rem title and the same term inside a 1rem hint both come out
 * at 72% of their surroundings.
 */
export const EN = "text-[0.72em] font-medium";

/**
 * A Latin run.
 *
 * It MUST start with a letter, so bare figures ("22%", "4.2/5", "0.82$") are
 * never caught: they are not English, and shrinking the very numbers a deck is
 * teaching would misrepresent them. Wrapping parentheses are absorbed so
 * "(Conversion Rate)" scales as one unit rather than three.
 */
const LATIN_RUN = /(\(?[A-Za-z][A-Za-z0-9&.'’+-]*(?:[ /]+[A-Za-z0-9&.'’+-]+)*\)?)/g;

/**
 * A compound figure: digits joined by a separator, or carrying a % or $.
 *
 * ⚠️ These MUST be direction-isolated or the bidi algorithm silently reorders
 * them inside RTL prose. Measured in Chrome with the deck's own container:
 *
 *     "3-6 أشهر"  renders as  "6-3 أشهر"
 *     "2-15"      renders as  "15-2"
 *     "22%"       renders as  "%22"
 *
 * A range that reads backwards is not a styling nit: it is the slide stating a
 * different fact from the one it was authored with. Hub has the same latent bug,
 * because `Ar` skips bare numbers (correctly, so they are never shrunk) and
 * nothing else isolates them.
 *
 * A single unbroken run of digits ("1240") is already unambiguous to bidi and is
 * deliberately NOT matched: wrapping it would add markup that changes nothing.
 */
// Deliberately consumes NO surrounding whitespace. An earlier version allowed an
// optional space before the % / $, which pulled the following space into the
// isolated span, where the isolate boundary collapsed it and ran the words
// together ("القيمة4.2/5هنا").
const FIGURE_RUN = /([$€£]?\d+(?:[.,:/–-]\d+)+[%$€£]?|[$€£]?\d+(?:[.,]\d+)?[%$€£])/g;

const warned = new Set<string>();

function warnInDev(t: string) {
  if (warned.has(t)) return;
  for (const v of findDashViolations(t)) {
    warned.add(t);
    console.warn(`[typography] em dash between Arabic text near: ${v.excerpt}`);
  }
}

/** Renders a string with its Latin runs stepped down to 0.72em. */
function Figures({ text }: { text: string }) {
  const parts = text.split(FIGURE_RUN);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          // dir="ltr" carries `unicode-bidi: isolate` from the UA stylesheet, which
          // is what actually stops the reordering. No size change: a figure must
          // never be stepped down.
          <span key={i} dir="ltr" className="whitespace-nowrap">{part}</span>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

export function Ar({ t }: { t?: string | null }) {
  if (!t) return null;
  if (import.meta.env.DEV) warnInDev(t);
  const parts = t.split(LATIN_RUN);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <span key={i} className={EN}>{part}</span>
        ) : (
          <Figures key={i} text={part} />
        ),
      )}
    </>
  );
}

/**
 * A number inside an RTL deck.
 *
 * Without `dir="ltr"` the bidi algorithm renders "1 / 6" as "6 / 1" and a slide
 * id "0-01" reversed. `tabular-nums` stops a counter jittering as it counts.
 */
export function Num({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span dir="ltr" className={cn("inline-block tabular-nums", className)}>
      {children}
    </span>
  );
}

/** Presenter script text: `**bold**` becomes bold, and both runs go through Ar. */
export function RichText({ text }: { text: string }) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <strong key={i} className="font-semibold text-text"><Ar t={part} /></strong>
        ) : (
          <Ar key={i} t={part} />
        ),
      )}
    </>
  );
}
